import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Snackbar, Alert,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, FormControlLabel, Checkbox, Tooltip
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../api/api';

const DocumentosFiscalesAlbaran = ({ idAlbaran, idCliente, onDocumentDeleted }) => {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal para eliminar
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [docAEliminar, setDocAEliminar] = useState(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Feedback Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

  const showSnackbar = (msg, severity = 'success') => {
    setSnackbar({ open: true, msg, severity });
  };

  // Carga de DTEs filtrados por el Albarán actual
  const fetchDocumentos = useCallback(async () => {
    if (!idAlbaran || idAlbaran === 'nuevo') {
      setDocumentos([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Petición al endpoint de listado enviando el ID del albarán
      const response = await api.get('/documentos_fiscales/listado', {
        params: { id_albaran: idAlbaran }
      });

      // Normalización de respuesta (paginada o array directo)
      const rawData = response.data?.data || (Array.isArray(response.data) ? response.data : []);
      setDocumentos(rawData);
    } catch (error) {
      console.error('Error al cargar documentos fiscales del albarán:', error);
      showSnackbar('Error al cargar los DTEs vinculados', 'error');
    } finally {
      setLoading(false);
    }
  }, [idAlbaran]);

  useEffect(() => {
    fetchDocumentos();
  }, [fetchDocumentos]);

  // Manejo de eliminación
  const handleOpenDelete = (doc) => {
    setDocAEliminar(doc);
    setConfirmChecked(false);
    setOpenDeleteDialog(true);
  };

  const confirmarEliminacion = async () => {
    if (!docAEliminar) return;

    setDeleting(true);
    try {
      await api.delete(`/documentos_fiscales/${docAEliminar.id}`);
      showSnackbar('Documento eliminado correctamente', 'success');
      setOpenDeleteDialog(false);
      setDocAEliminar(null);

      // Actualizar listado local y notificar al componente padre
      fetchDocumentos();
      if (onDocumentDeleted) onDocumentDeleted();
    } catch (error) {
      console.error('Error al eliminar documento fiscal:', error);
      showSnackbar(
        error.response?.data?.detail || 'No se pudo eliminar el documento',
        'error'
      );
    } finally {
      setDeleting(false);
    }
  };

  // Helper para renderizar tipo de DTE
  const renderTipoDte = (tipo) => {
    const mapaTipos = {
      '01': { label: 'Factura', color: 'primary' },
      '03': { label: 'CCF', color: 'secondary' },
      '05': { label: 'Nota Crédito', color: 'warning' },
      '06': { label: 'Nota Débito', color: 'info' },
      '14': { label: 'Sujeto Excluido', color: 'default' }
    };
    const conf = mapaTipos[tipo] || { label: `DTE ${tipo || ''}`, color: 'default' };
    return <Chip label={conf.label} size="small" variant="outlined" color={conf.color} />;
  };

  // Helper para renderizar estado de vinculación
  const renderEstadoVinculacion = (estado) => {
    switch (estado) {
      case 'VINCULADO_AUTO':
      case 'VINCULADO_MANUAL':
      case 'VINCULADO':
        return <Chip label={estado} color="success" size="small" />;
      case 'PENDIENTE_MANUAL':
        return <Chip label="PENDIENTE" color="warning" size="small" />;
      default:
        return <Chip label={estado || 'DESCONOCIDO'} size="small" />;
    }
  };

  return (
    <Paper sx={{ p: 2, mt: 3, borderRadius: 2 }} elevation={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <DescriptionIcon color="primary" />
        <Typography variant="h6" fontWeight="bold">
          Documentos Fiscales Vinculados (DTEs)
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={30} />
        </Box>
      ) : documentos.length === 0 ? (
        <Alert severity="info">
          No hay documentos fiscales (DTEs) vinculados a este albarán.
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nº Control / Cód. Generación</TableCell>
                <TableCell>Tipo DTE</TableCell>
                <TableCell>Fecha Emisión</TableCell>
                <TableCell>Receptor</TableCell>
                <TableCell align="right">Monto Total</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documentos.map((doc) => {
                const monto = doc.monto_total_pagar ?? doc.monto_total ?? 0;
                return (
                  <TableRow key={doc.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {doc.numero_control || 'S/N'}
                      </Typography>
                      {doc.codigo_generacion && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {doc.codigo_generacion}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{renderTipoDte(doc.tipo_dte)}</TableCell>
                    <TableCell>{doc.fecha_emision || 'N/A'}</TableCell>
                    <TableCell>{doc.nombre_receptor || 'Cliente General'}</TableCell>
                    <TableCell align="right">
                      ${Number(monto).toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      {renderEstadoVinculacion(doc.estado_vinculacion)}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        {doc.pdf_url && (
                          <Tooltip title="Ver Documento PDF">
                            <IconButton
                              size="small"
                              color="primary"
                              component="a"
                              href={doc.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <PdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Eliminar vinculación">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDelete(doc)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Diálogo Confirmar Eliminación */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => !deleting && setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>Eliminar Documento Fiscal</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            ¿Estás seguro de que deseas eliminar el DTE con Nº de Control{' '}
            <strong>{docAEliminar?.numero_control || docAEliminar?.codigo_generacion}</strong>?
          </Typography>
          <Alert severity="warning">
            Esta acción desvinculará el documento del albarán de forma permanente.
          </Alert>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                color="error"
                disabled={deleting}
              />
            }
            label="Entiendo que esta acción es irreversible"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={confirmarEliminacion}
            variant="contained"
            color="error"
            disabled={!confirmChecked || deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default DocumentosFiscalesAlbaran;